import React, { FunctionComponent, useEffect, useState } from 'react'
import { useQuery } from 'urql'
import { Spinner } from 'reactstrap'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid'
import { Link as RouterLink } from 'react-router-dom'

import * as SC from 'components/TailwindUIToolkit/StyledComponents'
import { REQUEST_POLICY } from 'constants/urql'
import { CustomerHeaderInfoByUserIdDocument as queryDoc } from 'graphql/generated'
import { format, subHours } from 'date-fns'
import { mapContentToBadge } from 'components/TailwindUIToolkit/badges'
import { Customer } from '../types'
import DropDown from './DropDown'
import { getCloseLeadUrl } from 'lib/close'
import axios from 'lib/axios'

import { connect } from 'react-redux'
import { StoreState } from 'store'

type PageTitleComponentPropTypes = {
  customer: Customer
  triggerReexecute?: Function
}

type RelatedUser = {
  id: string
  first_name?: string | null
  last_name?: string | null
  name?: string | null
}

type MergeStatusUser = {
  id: string
  primary_user_id?: string | null
  primary_user?: RelatedUser | null
  merged_at?: string | null
  merged_by_admin?: RelatedUser | null
}

const getResponseName = (name?: string | null): string => {
  const trimmedName = name?.trim()

  if (
    !trimmedName ||
    ['No Name Provided', 'null null', 'undefined undefined'].includes(trimmedName)
  ) {
    return ''
  }

  return trimmedName
}

const fullName = (user?: RelatedUser | null) =>
  [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
  getResponseName(user?.name) ||
  'No Name Provided'

const PageTitle: FunctionComponent<React.PropsWithChildren<PageTitleComponentPropTypes>> = ({
  customer,
  triggerReexecute
}) => {
  const [createdAt, setCreatedAt] = useState<Date | null>(null)
  const [badgeStatus, setBadgeStatus] = useState<string>('')
  const [mergeStatusUser, setMergeStatusUser] = useState<MergeStatusUser | null>(null)

  const [{ data, fetching, error }, reexecuteQuery] = useQuery({
    query: queryDoc,
    variables: { id: customer.userId },
    requestPolicy: REQUEST_POLICY.CACHE_AND_NETWORK,
    context: React.useMemo(() => ({ suspense: false }), [])
  })

  useEffect(() => {
    if (triggerReexecute) {
      reexecuteQuery()
    }
  }, [triggerReexecute])

  useEffect(() => {
    const user = data?.users[0]
    if (!user) return

    setCreatedAt(subHours(new Date(user.created_at), 100))

    if (user.deleted_at) {
      setBadgeStatus('deleted')
    } else if (user.rental_users?.length) {
      setBadgeStatus('renter')
    } else if (
      // checks if any active rental applications associated with any of the users lead groups
      user.lead_group_users.some((lg: any) => lg.rental_applications.length)
    ) {
      setBadgeStatus('applied')
    } else if (user?.user_funnel_status?.archived_at) {
      setBadgeStatus('archived')
    } else if (!user.user_prequalification_data) {
      setBadgeStatus('needs_prequalification')
    } else if (user?.user_funnel_status?.lead_status) {
      setBadgeStatus(user.user_funnel_status.lead_status)
    }
  }, [data])

  useEffect(() => {
    let isCurrentRequest = true
    const requestedUserId = customer.userId

    setMergeStatusUser(null)

    axios
      .get<MergeStatusUser>(`/admin/users/${requestedUserId}`)
      .then(({ data }) => {
        if (isCurrentRequest && data.id === requestedUserId) {
          setMergeStatusUser(data)
        }
      })
      .catch(() => {
        if (isCurrentRequest) {
          setMergeStatusUser(null)
        }
      })

    return () => {
      isCurrentRequest = false
    }
  }, [customer.userId])

  if (fetching) {
    return <Spinner />
  }
  if (error) {
    return <SC.ErrorMessage>{error.message}</SC.ErrorMessage>
  }

  if (!data?.users[0]) {
    return null
  }

  const isArchived = data?.users[0]?.user_funnel_status?.archived_at
  const closeLeadUrl = getCloseLeadUrl(data?.users[0]?.close_lead_id)
  const primaryUserId = mergeStatusUser?.primary_user_id
  const mergedByName = mergeStatusUser?.merged_by_admin
    ? fullName(mergeStatusUser.merged_by_admin)
    : 'System'

  return (
    <>
      <div className='mt-2 flex items-center justify-between gap-4'>
        <div className='flex items-center gap-4'>
          <div className='m-0 overflow-x-hidden text-ellipsis whitespace-nowrap text-2xl font-bold sm:text-3xl'>
            {customer?.displayName ?? '...'}
          </div>
          {badgeStatus ? mapContentToBadge(badgeStatus) : null}
        </div>
        <div className='flex items-center gap-3'>
          {closeLeadUrl ? (
            <a
              className='inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 hover:no-underline'
              href={closeLeadUrl}
              rel='noopener noreferrer'
              target='_blank'
            >
              View lead in Close
              <ArrowTopRightOnSquareIcon aria-hidden='true' className='h-4 w-4' />
            </a>
          ) : null}
          <DropDown isArchived={isArchived} reexecuteQuery={reexecuteQuery} />
        </div>
      </div>
      {!!createdAt ? (
        <div className='mt-2 flex items-center text-sm leading-5 text-gray-500'>
          Created on {format(createdAt, 'MMMM d, yyyy')}
        </div>
      ) : null}
      {primaryUserId ? (
        <div className='mt-4 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-900'>
          This user was merged into{' '}
          <RouterLink className='font-semibold underline' to={`/users/${primaryUserId}`}>
            {fullName(mergeStatusUser?.primary_user)}
          </RouterLink>
          {mergeStatusUser?.merged_at
            ? ` on ${format(new Date(mergeStatusUser.merged_at), 'MM/dd/yyyy @ hh:mm a')}`
            : null}{' '}
          by {mergedByName}. Use the merged user profile instead.
        </div>
      ) : null}
    </>
  )
}

const mapStateToProps = (state: StoreState) => ({
  triggerReexecute: state.refreshComponents.reexecuteHeader
})

export default connect(mapStateToProps, null)(PageTitle)
